import { IntlShape, defineMessages } from 'react-intl';

export interface AmcrErrorDetail {
  error: string;
  path: string;
}

export const amcrFieldLabels = defineMessages({
  project: { id: 'amcrPas.preview.project', defaultMessage: 'PAS project' },
  finder: { id: 'amcrPas.preview.finder', defaultMessage: 'Finder' },
  type: { id: 'amcrPas.preview.type', defaultMessage: 'Find type' },
  material: { id: 'amcrPas.preview.material', defaultMessage: 'Material' },
  era: { id: 'amcrPas.preview.era', defaultMessage: 'Period' },
  context: { id: 'amcrPas.preview.context', defaultMessage: 'Find context' },
  depth: { id: 'amcrPas.preview.depth', defaultMessage: 'Depth (cm)' },
  count: { id: 'amcrPas.preview.count', defaultMessage: 'Count' },
  foundOn: { id: 'amcrPas.preview.foundOn', defaultMessage: 'Date found' },
  latitude: {
    id: 'amcrPas.preview.latitude',
    defaultMessage: 'Latitude (WGS 84)',
  },
  longitude: {
    id: 'amcrPas.preview.longitude',
    defaultMessage: 'Longitude (WGS 84)',
  },
  coordinates: {
    id: 'amcrPas.preview.coordinates',
    defaultMessage: 'Coordinates',
  },
  accessibility: {
    id: 'amcrPas.preview.accessibility',
    defaultMessage: 'Accessibility',
  },
  localization: {
    id: 'amcrPas.preview.localization',
    defaultMessage: 'Localization',
  },
  note: { id: 'amcrPas.preview.note', defaultMessage: 'Note' },
});

export const amcrPreviewMessages = defineMessages({
  depth: {
    id: 'amcrPas.problem.depth',
    defaultMessage: 'The depth must be a whole number of centimetres.',
  },
  count: {
    id: 'amcrPas.problem.count',
    defaultMessage: 'The count must be a positive whole number.',
  },
  finderName: {
    id: 'amcrPas.preview.finderInvalid',
    defaultMessage:
      'Choose a finder from the list or write a new one as "Surname, Given name".',
  },
  coordinates: {
    id: 'amcrPas.preview.coordinatesInvalid',
    defaultMessage: 'Enter both coordinates in WGS 84, or leave both empty.',
  },
  foundOn: {
    id: 'amcrPas.preview.foundOnInvalid',
    defaultMessage: 'The date found must be a valid past date.',
  },
  vocabulary: {
    id: 'amcrPas.preview.vocabularyInvalid',
    defaultMessage: 'Choose a value from the PAS list.',
  },
  projectId: {
    id: 'amcrPas.preview.projectInvalid',
    defaultMessage: 'Choose one of the site’s PAS projects.',
  },
});

const problemMessages = defineMessages({
  'site.amcrProjectIds': {
    id: 'amcrPas.problem.projectId',
    defaultMessage: 'The site has no PAS project ID.',
  },
  'find.type': {
    id: 'amcrPas.problem.type',
    defaultMessage: 'The find type is missing or has no PAS equivalent.',
  },
  'find.material': {
    id: 'amcrPas.problem.material',
    defaultMessage: 'The material is missing or has no PAS equivalent.',
  },
  'find.era': {
    id: 'amcrPas.problem.era',
    defaultMessage: 'The era is missing or has no PAS equivalent.',
  },
  'find.situationType': {
    id: 'amcrPas.problem.situationType',
    defaultMessage: 'The find context has no PAS equivalent.',
  },
  'find.depth': amcrPreviewMessages.depth,
  'find.count': amcrPreviewMessages.count,
  'find.foundBy': {
    id: 'amcrPas.problem.foundBy',
    defaultMessage:
      'The finder must be written as "Surname, Given name". If you are the finder, add your first and last name in the account settings.',
  },
  'find.amcrId': {
    id: 'amcrPas.problem.amcrId',
    defaultMessage: 'The find already has an AMCR ID.',
  },
  'amcr.notCreatedByAcolyte': {
    id: 'amcrPas.problem.notCreatedByAcolyte',
    defaultMessage:
      'Photos can only be added to finds that were created in PAS from Acolyte.',
  },
  'record.projectId': amcrPreviewMessages.projectId,
  'record.typeId': amcrPreviewMessages.vocabulary,
  'record.materialId': amcrPreviewMessages.vocabulary,
  'record.eraId': amcrPreviewMessages.vocabulary,
  'record.contextId': amcrPreviewMessages.vocabulary,
  'record.depth': amcrPreviewMessages.depth,
  'record.count': amcrPreviewMessages.count,
  'record.foundOn': amcrPreviewMessages.foundOn,
  'record.latitude': amcrPreviewMessages.coordinates,
  'record.finderName': amcrPreviewMessages.finderName,
});

/** Localizes known export problems; AMCR's own messages are shown as sent. */
export const getAmcrProblemMessage = (
  intl: IntlShape,
  { error, path }: AmcrErrorDetail
) => {
  const message = problemMessages[path as keyof typeof problemMessages];

  return message ? intl.formatMessage(message) : error;
};
